import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, MapPin, CheckCircle2, ArrowRight, Zap, ChevronRight, Settings, CircleHelp, LayoutGrid } from "lucide-react";
import useUserId from "@/hooks/useUserId";
import AvatarUploader from "@/components/AvatarUploader";
import { EXPERIENCES } from "@/experiences/experiencesData";
import "./ayaMenu.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AyaMenu({ open, onClose, liveActive = false }) {
  const navigate = useNavigate();
  const userId = useUserId();
  const [homeAddr, setHomeAddr] = useState(null);

  const userName = localStorage.getItem("aya_user_name") || localStorage.getItem("civ_user_name") || "Invité AYA";

  useEffect(() => {
    if (!open || !userId) return;
    axios.get(`${API}/v3/addresses`, { params: { user_id: userId } })
      .then((r) => {
        const list = r.data?.addresses || r.data || [];
        const home = list.find?.((a) => a.is_home) || list[0] || null;
        setHomeAddr(home);
      })
      .catch(() => setHomeAddr(null));
  }, [open, userId]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  const go = (route) => { onClose(); navigate(route); };
  const addrLabel = homeAddr ? (homeAddr.description || homeAddr.name || homeAddr.commune || "Mon adresse AYA") : null;

  return (
    <>
      <div className="am-overlay" data-testid="aya-menu-overlay" onClick={onClose} />
      <aside className="am-panel" data-testid="aya-menu-panel" role="dialog" aria-label="Menu AYA">
        <div className="am-head">
          <img src="/aya/splash.png" alt="Masque AYA" className="am-mask" />
          <div>
            <div className="am-title">AYA</div>
            <div className="am-sub1">L'AFRIQUE AU QUOTIDIEN</div>
            <div className="am-sub2">Rechercher&nbsp;&nbsp;·&nbsp;&nbsp;Comprendre&nbsp;&nbsp;·&nbsp;&nbsp;Agir</div>
          </div>
          <button className="am-close" data-testid="aya-menu-close" aria-label="Fermer le menu" onClick={onClose}>
            <X size={17} strokeWidth={2.6} />
          </button>
        </div>

        <div className="am-profile" data-testid="aya-menu-profile">
          <div className="row">
            <AvatarUploader size={58} name={userName} testid="aya-menu-avatar" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 data-testid="aya-menu-username">{userName}</h3>
              <div className="p1">Mon profil &amp; mon adresse</div>
              <div className="addr" data-testid="aya-menu-address">
                <MapPin size={12} color="#34D399" />
                {addrLabel || "Adresse AYA non configurée"}
              </div>
              {addrLabel
                ? <span className="am-badge" data-testid="aya-menu-addr-badge"><CheckCircle2 size={11} /> Adresse AYA configurée</span>
                : <span className="am-badge" style={{ color: "#FBBF24", borderColor: "rgba(245,196,69,0.45)", background: "rgba(245,196,69,0.12)" }}>Configurer mon adresse</span>}
            </div>
          </div>
          <button className="am-profile-btn" data-testid="aya-menu-profile-btn" onClick={() => go("/mon-compte")}>
            Voir mon profil <ArrowRight size={13} />
          </button>
        </div>

        <div className="am-sechead">
          <span className="zap"><Zap size={14} color="#34D399" fill="#34D399" /></span>
          <h2>Les expériences AYA</h2>
        </div>
        <div className="am-secsub">Découvrez, participez, apprenez, partagez...</div>

        <div className="am-grid" data-testid="aya-menu-experiences">
          {EXPERIENCES.map((e) => (
            <button key={e.id} className="am-exp" data-testid={`aya-menu-exp-${e.id}`} onClick={() => go(e.route)}>
              <img src={e.img} alt="" className="bg" loading="lazy" />
              {e.badge && liveActive && <span className="am-live-badge" data-testid="aya-menu-live-badge">● EN DIRECT</span>}
              <e.Icon size={22} color={e.color} strokeWidth={2.2} />
              <h3>{e.title}</h3>
              <p>{e.desc}</p>
              <span className="arrow"><ChevronRight size={12} color="#fff" /></span>
            </button>
          ))}
        </div>

        <button className="am-all-btn" data-testid="aya-menu-all-experiences" onClick={() => go("/experiences")}>
          <LayoutGrid size={14} /> Voir toutes les expériences <ArrowRight size={13} />
        </button>

        <div className="am-sep" />
        <button className="am-row" data-testid="aya-menu-settings" onClick={() => go("/mon-compte")}>
          <span className="ic"><Settings size={15} color="#CFE5D6" /></span>
          <span style={{ flex: 1 }}><h4>Paramètres</h4><p>Compte, confidentialité, préférences</p></span>
          <ChevronRight size={15} color="#7C9384" />
        </button>
        <button className="am-row" data-testid="aya-menu-support" onClick={() => go(`/aya-prive?ctx=${encodeURIComponent("J'ai besoin d'aide sur l'application AYA (centre d'aide / signaler un problème).")}`)}>
          <span className="ic"><CircleHelp size={15} color="#CFE5D6" /></span>
          <span style={{ flex: 1 }}><h4>Aide &amp; support</h4><p>Centre d'aide, contact, signaler un problème</p></span>
          <ChevronRight size={15} color="#7C9384" />
        </button>

        <div className="am-pro" data-testid="aya-menu-pro-card">
          <div className="t">
            <h3>Devenez Pro</h3>
            <p>Développez votre activité avec AYA</p>
            <button className="am-pro-btn" data-testid="aya-menu-pro-btn" onClick={() => go("/inscription-pro")}>
              Créer mon espace Pro <ArrowRight size={12} />
            </button>
          </div>
          <img src="/aya/experiences/exp_pro.jpg" alt="Commerçante AYA Pro" loading="lazy" />
        </div>
      </aside>
    </>
  );
}
